/**
 * Visit Tracker Utility
 * 
 * Lightweight client-side trigger for dashboard visit tracking.
 * Sends minimal data to the API — all geolocation is resolved server-side.
 * No browser permission prompts, no consent banners.
 * 
 * Debounces to one tracking call per browser session.
 */

const SESSION_VISIT_KEY = 'inkspot_dashboard_visited';

// API base URL for the track-visit endpoint
const API_BASE = import.meta.env.VITE_API_URL || 'https://inkspot-api.vercel.app';

/**
 * Detect device type from user agent
 * @returns {'desktop' | 'mobile' | 'tablet'}
 */
function getDeviceType() {
    if (typeof window === 'undefined') return 'desktop';

    const ua = navigator.userAgent;
    if (/tablet|ipad|playbook|silk/i.test(ua)) return 'tablet';
    if (/mobile|iphone|ipod|android|blackberry|opera mini|iemobile/i.test(ua)) return 'mobile';
    return 'desktop';
}

/**
 * Check if dashboard visit has already been tracked this session
 * @returns {boolean}
 */
function hasTrackedThisSession() {
    if (typeof window === 'undefined') return true;
    return sessionStorage.getItem(SESSION_VISIT_KEY) === 'true';
}

/**
 * Mark dashboard visit as tracked for this session
 */
function markTrackedThisSession() {
    if (typeof window === 'undefined') return;
    sessionStorage.setItem(SESSION_VISIT_KEY, 'true');
}

/**
 * Track a user's visit to the dashboard page.
 * 
 * Sends minimal data to the API endpoint which handles:
 * - IP extraction from request headers
 * - IP-based geolocation (lat/lng/address)
 * - Database insertion
 * 
 * Only tracks once per browser session (debounced via sessionStorage).
 * 
 * @param {string} userId - The authenticated user's ID
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function trackDashboardVisit(userId) {
    // Guard: only track once per session
    if (hasTrackedThisSession()) {
        return { success: false, error: 'Already tracked this session' };
    }

    // Guard: require user ID
    if (!userId) {
        return { success: false, error: 'No user ID' };
    }

    try {
        // Send minimal data — IP and geolocation are resolved server-side
        const response = await fetch(`${API_BASE}/api/analytics/track-visit`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                user_id: userId,
                page_path: '/dashboard',
                user_agent: navigator.userAgent,
                device_type: getDeviceType(),
            }),
        });

        if (!response.ok) {
            throw new Error(`API returned ${response.status}`);
        }

        // Mark as tracked for this session
        markTrackedThisSession();

        return { success: true };
    } catch (error) {
        console.error('Dashboard visit tracking error:', error);
        return { success: false, error: error.message };
    }
}
